export const staticCacheName = "continuum-go-cache-static-v1";
export const noAuthCacheName = "continuum-go-cache-no-auth-v1";

const apiUrl = process.env.REACT_APP_API_URL;
const API_HOST = new URL(apiUrl).host;
const GO_APP_API_SEGMENT = '/continuum-go';
const API_CACHE_POST_ENDPOINTS = [
    '/config',
]
/** Tenantless / login flows — must not be offline-cached. */
const API_GET_NON_CACHE_ENDPOINTS = [
    '/token/deployments',
    '/config/tenants/',
]
const SITE_PAGES = [
    '/',
    '/controlled-documents',
    '/controlled-documents/:id',
    '/training-records',
    '/training-records/:id',
    '/forms',
    '/forms/:id',
    '/timelog',
    '/payslips',
    '/payslips/:id',
    '/absences',
    '/absences/create',
    '/expenses',
    '/expenses/create',
    '/lms',
    '/service-orders',
    '/service-orders/:id',
    '/hr',
    '/docs',
    '/work',
]

export const STATIC_ASSETS = [
    '/',
    '/index.html',
    '/images/favicon/favicon.svg',
    '/images/favicon/favicon-96x96.png',
    '/images/favicon/favicon.ico',
    '/images/favicon/apple-touch-icon.png',
    '/images/favicon/site.webmanifest',
    '/images/logo-no-text.svg',
    '/images/logo-text.svg',
    '/static/js/bundle.js',
    '/ej2-pdfviewer-lib/pdfium.js',
    '/ej2-pdfviewer-lib/pdfium.wasm',
];

export const STATIC_ASSET_PATTERNS = [
    '/static/js/.*',  // Matches any file in static/js directory
    '/static/css/.*', // Matches any file in static/css directory
    '/static/media/.*', // Matches any file in static/media directory
    '/images/.*', // Matches any file in images directory
];

export const isStaticAsset = (path) => {
    // First check exact matches
    if (STATIC_ASSETS.includes(path)) {
        return true;
    }

    // Then check patterns
    return STATIC_ASSET_PATTERNS.some(pattern => {
        const regexPattern = pattern.replace(/\.\*/g, '[^/]*');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(path);
    });
}

export const isHashedStaticAsset = (path) => {
    const isStaticPath =
        path.startsWith('/static/js/') ||
        path.startsWith('/static/css/') ||
        path.startsWith('/static/media/')
    if (!isStaticPath) {
        return false
    }
    return /\.[a-f0-9]{8,}\./i.test(path)
}

/** Strip v2 base and tenant prefix so `/config` matches `…/continuum-go/config` or `…/_tenant/continuum-go/config`. */
export const removeGoAppPathForCacheMatch = (path) => {
    const goIdx = path.indexOf(GO_APP_API_SEGMENT);
    if (goIdx < 0) {
        return path;
    }
    return path.slice(goIdx + GO_APP_API_SEGMENT.length);
}

const getTenantUserCacheName = (tenantID, userId) => {
    return `continuum-go-cache-api-${tenantID}-${userId}`;
}
// Generate a tenant/user-specific cache key
export function getTenantUserCacheNameFromRequest(request) {
    // both the tenant and user are in the Authorization header
    const authHeader = request.headers.get('Authorization');
    if (!authHeader) {
        return noAuthCacheName;
    }
    const token = authHeader.split(' ')[1];
    if (!token) {
        return noAuthCacheName;
    }
    return getTenantUserCacheNameFromToken(token);
}
export function getTenantUserCacheNameFromToken(token) {
    // both the tenant and user are in the Authorization header
    if (!token) {
        return noAuthCacheName;
    }
    const tenantID = getTenantIdFromToken(token);
    const userId = getUserIdFromToken(token);
    return getTenantUserCacheName(tenantID, userId);
}
function getTenantIdFromToken(token) {
    const decodedToken = jwtDecode(token);
    const deployment = decodedToken.deployment;
    if (!deployment) {
        return "unknown";
    }
    const parts = deployment.split('|');
    if (parts.length < 3) {
        return "unknown";
    }
    return parts[2];
}
function getUserIdFromToken(token) {
    const decodedToken = jwtDecode(token);
    return decodedToken.username;
}
function jwtDecode(token) {
    return JSON.parse(atob(token.split('.')[1]));
}

// Simple hash function for the request body
export async function hashBody(body) {
    const encoder = new TextEncoder();
    const data = encoder.encode(body);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Some functions
export const isSitePage = (path) => {
    return SITE_PAGES.some(pagePattern => {
        const regexPattern = pagePattern.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(path);
    });
}
export const isApiRequest = (request) => {
    //check host against API_HOST, including port
    const host = new URL(request.url).host;
    return host === API_HOST;
}

/** Local IIS dev cert is often rejected inside the service worker fetch path. */
export const isLocalDevApiHost = () => {
    const host = API_HOST.toLowerCase();
    return host.startsWith('localhost:') || host.startsWith('127.0.0.1:');
}

export const isApiCacheableRequest = (request) => {
    if (!isApiRequest(request)) {
        return false;
    }
    if (request.method === 'GET') {
        let pathname = new URL(request.url).pathname;
        pathname = removeGoAppPathForCacheMatch(pathname);
        if (API_GET_NON_CACHE_ENDPOINTS.some((endpoint) => pathname === endpoint || pathname.startsWith(endpoint))) {
            return false;
        }
        return true;
    } else if (request.method === 'POST') {
        return API_CACHE_POST_ENDPOINTS.some(endpoint => {
            const regexPattern = endpoint.replace(/:[^/]+/g, '[^/]+');
            const regex = new RegExp(`^${regexPattern}$`);
            let pathname = new URL(request.url).pathname;
            pathname = removeGoAppPathForCacheMatch(pathname);
            return regex.test(pathname);
        });
    }
    return false;
}
export const getCacheKey = async (request) => {
    if (request.method === 'GET') {
        return request.url;
    } else if (request.method === 'POST') {
        const body = await request.clone().text();
        const hash = await hashBody(body);
        return `${request.url}-${hash}`;
    }
}
export const getApiCacheRequests = async (token) => {
    const cacheName = getTenantUserCacheNameFromToken(token);
    const cache = await caches.open(cacheName);
    const keys = await cache.keys();
    return keys;
}

async function cacheEndpoint(cache, api, endpoint) {
    try {
        const endpointInstance = api.endpoints[endpoint]();
        // Mark as background to prevent foreground handlers from running
        const response = await endpointInstance.execute(api, null, null, { isBackground: true });
        // Convert JSON to Response object
        const responseObject = new Response(JSON.stringify(response), {
            headers: { 'Content-Type': 'application/json' }
        });
        await cache.put(endpointInstance.url, responseObject);
        return true;
    } catch (error) {
        console.error(`Error caching ${endpoint}:`, error);
        return false;
    }
}

export async function onLogin(token, api) {
    const cacheName = getTenantUserCacheNameFromToken(token);
    const cache = await caches.open(cacheName);

    try {
        // Cache all endpoints
        const results = await Promise.all([
            cacheEndpoint(cache, api, 'additionalPaymentsPending'),
            cacheEndpoint(cache, api, 'additionalPaymentsProcessed'),
            cacheEndpoint(cache, api, 'additionalPaymentTypes'),
            cacheEndpoint(cache, api, 'absenceTypes'),
            cacheEndpoint(cache, api, 'holidayEntitlements'),
            cacheEndpoint(cache, api, 'unassignedAbsences'),
            cacheEndpoint(cache, api, 'payslips'),
            cacheEndpoint(cache, api, 'timelogStatus'),
            cacheEndpoint(cache, api, 'serviceOrdersToday'),
            cacheEndpoint(cache, api, 'serviceOrdersFuture'),
            cacheEndpoint(cache, api, 'controlledDocuments'),
            cacheEndpoint(cache, api, 'trainingRecords'),
            cacheEndpoint(cache, api, 'continuumForms')
        ]);

        if (results.every(success => success)) {
            console.log('Successfully cached all initial data on login');
        } else {
            console.warn('Some endpoints failed to cache on login');
        }
    } catch (error) {
        console.error('Error during login caching:', error);
    }
}